/**
 * ColorRow — a colour as a swatch you click plus a hex you can paste.
 *
 * Both halves matter: the swatch is how someone with a colour in mind picks
 * it, the text box is how someone with a brand hex in a document gets it in
 * without eyeballing a wheel. The text box is TOLERANT — "FFD500", "#ffd500"
 * and "#fd5" all land on the same colour (`normalizeHex`, the inspector's
 * own parser, so the two surfaces can never disagree about what a hex is);
 * anything unparseable simply never commits and the box snaps back.
 *
 * Timing: dragging the native picker previews (no journal entry per frame),
 * blur commits ONE entry — the inspector's contract.
 */
import { useEffect, useRef, useState } from "react";

import type { EditPatch } from "../../../../../src/lib/clipEditState.ts";
import { normalizeHex } from "../../StudioInspector.tsx";
import { fieldLabelStyle } from "../../styles.ts";
import type { EssentialRowProps } from "./types.ts";

export function ColorRow(
  p: {
    label: string;
    value: string;
    toPatch: (hex: string) => EditPatch;
    commitLabel: string;
  } & EssentialRowProps,
): React.JSX.Element {
  const { label, value, toPatch, commitLabel, onPreview, onCommit } = p;
  const canonical = normalizeHex(value) ?? "#ffffff";
  const [draft, setDraft] = useState(canonical);
  const [typing, setTyping] = useState(false);
  /** The value the current gesture started from — what "did anything
   *  actually change?" is measured against on blur. */
  const startRef = useRef(canonical);

  // External resync (undo, a preset, a chat patch) — only while the box is
  // idle, so a repaint can never clobber a half-typed hex.
  useEffect(() => {
    if (!typing) setDraft(canonical);
  }, [canonical, typing]);

  const commit = (hex: string): void => {
    if (hex === startRef.current) return;
    startRef.current = hex;
    onCommit(toPatch(hex), `${commitLabel} → ${hex.toUpperCase()}`);
  };

  return (
    <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
      <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>{label}</span>
      <div style={{ display: "flex", gap: 7, alignItems: "center", minWidth: 0 }}>
        <input
          type="color"
          value={canonical}
          aria-label={label}
          onFocus={() => { startRef.current = canonical; }}
          onChange={(e) => onPreview(toPatch(e.target.value.toLowerCase()))}
          onBlur={(e) => commit(e.target.value.toLowerCase())}
          style={{
            width: 42,
            height: 30,
            flexShrink: 0,
            borderRadius: 8,
            border: "1px solid var(--ql-border)",
            background: "transparent",
            cursor: "pointer",
            padding: 2,
          }}
        />
        <input
          type="text"
          value={typing ? draft : canonical.toUpperCase()}
          spellCheck={false}
          aria-label={`${label} hex`}
          onFocus={() => { setTyping(true); setDraft(canonical.toUpperCase()); startRef.current = canonical; }}
          onChange={(e) => {
            setDraft(e.target.value);
            const hex = normalizeHex(e.target.value);
            if (hex) onPreview(toPatch(hex));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(startRef.current);
              onPreview(toPatch(startRef.current));
              e.currentTarget.blur();
            }
          }}
          onBlur={() => {
            setTyping(false);
            const hex = normalizeHex(draft);
            // Unparseable → snap back to the live value AND revert the
            // preview; a typo must never leave the canvas on a half-colour.
            if (!hex) {
              setDraft(canonical);
              onPreview(toPatch(startRef.current));
              return;
            }
            setDraft(hex);
            commit(hex);
          }}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "7px 9px",
            borderRadius: 8,
            border: "1px solid var(--ql-border)",
            background: "rgba(255,255,255,.03)",
            color: "var(--ql-fg)",
            fontFamily: "var(--ql-font-mono)",
            fontSize: 11.5,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  );
}
